import React, { useState, useEffect, useCallback } from 'react'
import { prefixLocale, getLink } from '@helpers/env'
import { SizingWidgetData } from '@helpers/products/sizingWidgetData'
import Translations from '@helpers/translations'
import { ReactComponent as CloseIcon } from '@svgs/keyline/close.svg'
import SizingWidget from '@components/product/SizingWidget'
import internationalTableData from './size_chart_international.json'
import SizeChartHeader from './SizeChartHeader'
import SizeChartDropDown from './SizeChartDropDown'
import SizeChartTableSelector, {
  InternationalTableRowsProperties
} from './SIzeChartTableSelector'
import HowToMeasure from './HowToMeasure'
import styles from './styles.module.scss'
import { EmptyObject } from 'types/helpers'
import { NextRouter } from 'next/router'
import { getSizeChart } from '@api/sizeChart'
import { SIZE_CHART_TOP_SORT_ORDER } from '@consts/index'
import {
  getDefaultSizeChartProductType,
  SIZE_CHART_CATEGORIES,
  SizeChartCollection
} from '@helpers/sizeChart'

type SizeChart = {
  items: {
    build: string | null
    cut: string | null
    leg: string | null
    product_measurement_type: string
    product_type: string
    measurements: { key: string; value: number }[]
  }[]
}

type RouterQueryProps = {
  pagesHandle: string
  type?: string
  preview?: string
}

const TABS_MAP = {
  a: Translations.localise('pages.size-chart.tabs.build-a'),
  b: Translations.localise('pages.size-chart.tabs.build-b'),
  c: Translations.localise('pages.size-chart.tabs.build-c'),
  slim: Translations.localise('pages.size-chart.tabs.slim'),
  straight: Translations.localise('pages.size-chart.tabs.straight'),
  standard: Translations.localise('pages.size-chart.tabs.standard'),
  dadletic: Translations.localise('pages.size-chart.tabs.dadletic'),
  sizes: Translations.localise('pages.size-chart.tabs.sizes')
} as const

type Measurement = {
  key: string
  value: number
}

type SizeChartItem = {
  build: string | null
  cut: string
  leg: string | null
  measurements: Measurement[]
  product_measurement_type: string
  product_type: string
}

export type SizeChartData = {
  items: SizeChartItem[]
}

function transformSizeChartData(sizeChartData: SizeChartData) {
  return {
    ...sizeChartData,
    items: sizeChartData?.items.map(item => {
      return {
        ...item,
        measurements: item.measurements.map(measurement => ({
          ...measurement,
          key: measurement.key.replace(/\s*inch/i, '')
        }))
      }
    })
  }
}

function getProductBuild(
  selectedProductSizeChart: SizeChart | undefined
): string {
  const availableTabs = new Set(
    selectedProductSizeChart?.items.map(({ build, cut, leg }) =>
      (build || cut || leg)?.toLowerCase()
    )
  )

  return Object.keys(TABS_MAP).find(tab => availableTabs.has(tab)) || 'a'
}

function getParam(
  param: string,
  router: NextRouter,
  pagesHandle: string,
  sizeChartCollection: SizeChartCollection,
  selectedSizeChart?: SizeChart
) {
  const path = router.asPath.split('?')
  const index = pagesHandle.indexOf('-size-chart')
  const productCategory = index > -1 ? pagesHandle.slice(0, index) : 'trousers'
  const defaultProductType = getDefaultSizeChartProductType(
    sizeChartCollection,
    productCategory
  )
  const productType =
    path.length > 1 && path[1].includes('type')
      ? decodeURI(path[1].split('=')[1])
      : selectedSizeChart?.items[0]?.product_type || defaultProductType

  const buildType = getProductBuild(selectedSizeChart)

  if (param === 'category') {
    return productCategory
  }

  if (param === 'product') {
    return productType
  }

  if (param === 'build') {
    return buildType
  }

  return undefined
}

function getInternationalTableRows(
  productCategory: string | undefined,
  selectedProduct: string
) {
  switch (productCategory) {
    case 'tops':
      return (
        internationalTableData.tops[
          selectedProduct as keyof typeof internationalTableData.tops
        ]?.table || internationalTableData.tops.default.table
      )

    case 'underwear':
      return (
        internationalTableData.underwear[
          selectedProduct as keyof typeof internationalTableData.underwear
        ]?.table || internationalTableData.underwear.default.table
      )

    case 'accessories':
      if (/socks/.test(selectedProduct)) {
        const socksTable = (
          internationalTableData as {
            socks?: { default?: { table: InternationalTableRowsProperties[] } }
          }
        ).socks?.default?.table
        if (socksTable) return socksTable
      }
      break

    default:
      break
  }

  return internationalTableData.trousers?.default.table || []
}

interface SizeChartTableStructure {
  tableColumns: string[]
  tableRows: string[][]
  tableTabs:
    | {
        [key: string]: string
      }
    | EmptyObject
}

function getSizeChartTableStructure(
  selectedProductSizeChart: SizeChart | undefined,
  selectedBuildType: string,
  productCategory: string
): SizeChartTableStructure {
  const emptyTable = {
    tableColumns: [],
    tableRows: [],
    tableTabs: {}
  }

  try {
    if (!selectedProductSizeChart) return emptyTable

    const sizeChartItems = selectedProductSizeChart.items.filter(
      ({ build, cut, leg }) => {
        return [
          build?.toLowerCase(),
          cut?.toLowerCase(),
          leg?.toLowerCase()
        ].includes(selectedBuildType?.toLowerCase())
      }
    )

    if (sizeChartItems.length === 0) return emptyTable

    const tableColumns = sizeChartItems[0].measurements
      .map(({ key }) => key)
      .sort((a, b) => a.localeCompare(b))
      .sort(
        (a, b) =>
          SIZE_CHART_TOP_SORT_ORDER.indexOf(a) -
          SIZE_CHART_TOP_SORT_ORDER.indexOf(b)
      )

    const tableRows = sizeChartItems.map(
      ({ measurements, product_measurement_type }) => {
        const values = measurements
          .sort((a, b) => a.key.localeCompare(b.key))
          .sort(
            (a, b) =>
              SIZE_CHART_TOP_SORT_ORDER.indexOf(a.key) -
              SIZE_CHART_TOP_SORT_ORDER.indexOf(b.key)
          )
          .map(({ value }) => value.toString())

        return [product_measurement_type, ...values]
      }
    )

    const tabs: Record<string, string> = {}

    const tabsOrder = Object.keys(TABS_MAP)
    const measurementTabs = selectedProductSizeChart.items
      .map(({ build, cut, leg }) => (build || cut || leg)?.toLowerCase())
      .filter(Boolean)
      .sort((a, b) => tabsOrder.indexOf(a) - tabsOrder.indexOf(b))

    new Set(measurementTabs).forEach(key => {
      if (key && TABS_MAP[key as keyof typeof TABS_MAP]) {
        tabs[key] =
          productCategory.toLowerCase() === 'tops' && key === 'straight'
            ? TABS_MAP.standard
            : TABS_MAP[key as keyof typeof TABS_MAP]
      }
    })

    return {
      tableColumns: ['Spoke Size', ...tableColumns],
      tableRows,
      tableTabs: tabs
    }
  } catch (err) {
    console.error(err)
    return emptyTable
  }
}

const SizeChart = ({
  router,
  pagesHandle,
  sizingWidgetData,
  sizeChartCollection = {}
}: {
  router: NextRouter
  pagesHandle: string
  sizingWidgetData?: SizingWidgetData | EmptyObject
  sizeChartCollection?: SizeChartCollection
}) => {
  const { type: productType } = router.query as RouterQueryProps
  const [selectedSizeChart, setSelectedSizeChart] = useState<SizeChart>()
  const [productCategory, setProductCategory] = useState(
    getParam(
      'category',
      router,
      pagesHandle,
      sizeChartCollection,
      selectedSizeChart
    ) || 'trousers'
  )
  const [selectedBuildType, setSelectedBuildType] = useState('')
  const [selectedProduct, setSelectedProduct] = useState('')

  const selectedSizeChartCollection =
    sizeChartCollection[productCategory?.toLowerCase()] || {}

  const listOfProductTypes = Object.keys(selectedSizeChartCollection)

  // Only offer categories that have at least one size chart
  const categoriesWithSizeCharts = SIZE_CHART_CATEGORIES.filter(
    category => Object.keys(sizeChartCollection[category] || {}).length
  )

  const internationalTableRows = getInternationalTableRows(
    productCategory,
    selectedProduct
  )

  const { tableColumns, tableRows, tableTabs } = getSizeChartTableStructure(
    selectedSizeChart,
    selectedBuildType,
    productCategory
  )

  const handleSetProductCategory = (category: string) => {
    const firstProductOfCategory = getDefaultSizeChartProductType(
      sizeChartCollection,
      category
    )

    const buildType = getProductBuild(selectedSizeChart)
    setSelectedBuildType(buildType)
    setSelectedProduct(firstProductOfCategory)
    setProductCategory(category)
  }

  const handleSetSelectedProduct = (productType: string) => {
    const buildType = getProductBuild(selectedSizeChart)
    setSelectedBuildType(buildType)
    setSelectedProduct(productType)
  }

  const updateSizeChart = useCallback(
    async (productType: string) => {
      const { sizeChart } = await getSizeChart(productType)

      if (sizeChart) {
        const sizeChartTransformed = transformSizeChartData(sizeChart)
        setSelectedSizeChart(sizeChartTransformed)
        setSelectedBuildType(
          getParam(
            'build',
            router,
            pagesHandle,
            sizeChartCollection,
            sizeChartTransformed
          ) || ''
        )
      }
    },
    [router, pagesHandle, sizeChartCollection]
  )

  useEffect(() => {
    const product = getParam(
      'product',
      router,
      pagesHandle,
      sizeChartCollection
    )
    if (product) {
      setSelectedProduct(product)
      updateSizeChart(product)
    }
  }, [router, pagesHandle, sizeChartCollection, updateSizeChart])

  useEffect(() => {
    if (!!selectedProduct) {
      const url = `${prefixLocale()}/pages/${productCategory}-size-chart${`?type=${selectedProduct}`}`
      window.history.replaceState(null, '', url)
      updateSizeChart(selectedProduct)
    }
  }, [productCategory, selectedProduct, updateSizeChart])

  const goBack = () => {
    if (
      document.referrer.includes(window.location.hostname) &&
      document.referrer !== window.location.href
    ) {
      window.location.href = document.referrer
      return
    }
    window.location.href = getLink('/')
  }

  return (
    <>
      <div className={styles.sizeChart}>
        <div className={styles.sizeChart__container}>
          <div className={styles.sizeChart__wrapper}>
            <button
              type="button"
              className={styles.sizeChart__close}
              onClick={goBack}
            >
              <CloseIcon />
            </button>
            <SizeChartHeader
              options={categoriesWithSizeCharts}
              productCategory={productCategory}
              setProductCategory={handleSetProductCategory}
            />
            <div
              className={`${styles.sizeChart__dropdown} ${
                listOfProductTypes.length > 1
                  ? styles.sizeChart__dropdown_visible
                  : ''
              }`}
            >
              <SizeChartDropDown
                list={listOfProductTypes}
                collection={selectedSizeChartCollection}
                selectedProduct={selectedProduct}
                setSelectedProduct={handleSetSelectedProduct}
              />
            </div>
            <SizeChartTableSelector
              tabs={tableTabs}
              setSelectedBuildType={setSelectedBuildType}
              selectedBuildType={selectedBuildType}
              sizeChartColumn={tableColumns || []}
              sizeChartBuild={tableRows}
              productCategory={productCategory}
              productType={productType}
              internationalTableRows={internationalTableRows}
              isSizeChartLoaded={
                !!selectedSizeChart || !listOfProductTypes.length
              }
            />
          </div>
        </div>
      </div>
      {!!sizingWidgetData && (
        <SizingWidget
          sizingWidgetData={sizingWidgetData}
          productType={productType}
          productCategory={productCategory}
        />
      )}

      <HowToMeasure />
    </>
  )
}

export default SizeChart
